import classNames from 'classnames';
import React, { ChangeEvent, useRef, useState } from 'react';
import { Button } from 'src/common/button';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { TextInput } from 'src/common/form/text-input';
import { Popover } from 'src/common/ui/popover';
import { functionNoop } from 'src/utils/function/noop';
import { apiGenerateDalleImageFromPrompt } from 'src/utils/journeyApi';
import { getImageMetrics } from '../helpers/get-image-metrics';
import { PlaceHolderIcon, toPlaceHolderLabel } from '../helpers/placeholders';
import { BlockLayoutExtraParams } from '../layout-manager';
import { AspectRatio, BlockContent, BlockPlaceholderType } from '../types';

type Props = {
  placeholderType: BlockPlaceholderType;
  selected: boolean;
  onReplaceBlock: (content: BlockContent, layoutExtraParams: BlockLayoutExtraParams) => void;
};

export const PlaceholderBlock = ({ placeholderType, selected, onReplaceBlock }: Props) => {
  const openRef = useRef(functionNoop);
  const closeRef = useRef(functionNoop);
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(
    'https://images.unsplash.com/photo-1671455662528-5e10df0301b1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80'
  );
  const [dallePrompt, setDallePrompt] = useState('');
  const [loading, setLoading] = useState(false);

  const onImportImage = async () => {
    try {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const response = await fetch(imageUrl);
      const imageObjectURL = URL.createObjectURL(await response.blob());
      const { width, height, color } = await getImageMetrics(imageObjectURL);
      onReplaceBlock(
        {
          type: 'image',
          url: imageObjectURL,
          objectFit: 'contain',
          dominantColor: color,
        },
        {
          aspectRatio: {
            width,
            height,
          },
        }
      );
      setImageUrl('');
      setOpen(false);
    } catch (err) {
      console.error(err);
      alert('Could not load image');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitDallePrompt = async () => {
    try {
      setLoading(true);
      const response = await apiGenerateDalleImageFromPrompt(dallePrompt);
      const { url } = response;
      onReplaceBlock(
        {
          type: 'image',
          url,
          objectFit: 'contain',
        },
        {
          aspectRatio: {
            width: 1024,
            height: 1024,
          },
        }
      );
      setImageUrl(url);
      setOpen(false);
    } catch (err) {
      console.error(err);
      alert('Could not load image');
    } finally {
      setLoading(false);
    }
  };

  const onClick = () => {
    if (placeholderType === 'add-image-by-url') {
      setOpen(true);
    } else if (placeholderType === 'add-image') {
      console.log('add image');
    }
  };

  const renderPlaceholderContent = () => {
    const content = (
      <Button
        size='regular'
        variant='tertiary'
        className='pointer-events-none'
        label={toPlaceHolderLabel(placeholderType)}
        icon={<PlaceHolderIcon blockPlaceholderType={placeholderType} />}
        onClick={onClick}
      />
    );
    if (placeholderType === 'add-image-by-url') {
      return (
        <Popover
          open={open}
          placement='top-start'
          containerClassName='p-2'
          openRef={openRef}
          closeRef={closeRef}
          renderOrigin={() => content}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
        >
          <div className='flex flex-col space-y-4'>
            <div className='flex gap-2'>
              <TextInput
                containerClasses='w-[400px]'
                id='placeholder-image-url'
                autoFocus
                inputProps={{
                  type: 'text',
                  name: 'placeholder-image-url',
                  value: imageUrl,
                  placeholder: 'Enter an image URL',
                }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setImageUrl(e.target.value)}
              />
              <Button
                className='shrink-0'
                size='small'
                variant='primary'
                label='Import Image'
                disabled={!imageUrl}
                onClick={onImportImage}
              />
            </div>
            <div className='flex gap-2'>
              <TextInput
                containerClasses='w-[400px]'
                id='placeholder-image-dalle-prompt'
                autoFocus
                inputProps={{
                  type: 'text',
                  name: 'placeholder-image-dalle-prompt',
                  value: dallePrompt,
                  placeholder: 'Enter a DALL-e prompt',
                }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDallePrompt(e.target.value)}
              />
              <Button
                className='shrink-0'
                size='small'
                variant='primary'
                label='Generate DALL-E Image'
                disabled={!dallePrompt}
                onClick={onSubmitDallePrompt}
              />
            </div>
          </div>
        </Popover>
      );
    }
    return content;
  };

  return (
    <div
      className={classNames('w-full h-full flex flex-1 items-center justify-center rounded-2xl transition', {
        'neue-block-selection-ring-selected bg-bedrock-light-gray': selected,
        'bg-bedrock-light-gray/50 hover:bg-bedrock-light-gray': !selected,
      })}
    >
      {loading ? <LoadingSpinner /> : renderPlaceholderContent()}
    </div>
  );
};
